import React, { useState } from "react";
// Assuming 'react-bootstrap' components are available globally
import { Container, Row, Col, Form, InputGroup } from "react-bootstrap"; 
import axios from "axios";
import { Mail, Key, Lock, Send, RotateCcw, CheckCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || ''; 

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // State for flow: 1: Enter Email, 2: Enter OTP/New Pass, 3: Success
  const [step, setStep] = useState(1); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(""); 
  
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0); 

  // --- Step 1 Handlers: Request OTP ---
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const endpoint = API_URL + "/api/generate-otp"; 
      const resp = await axios.post(endpoint, { 
        email,
        purpose: 'reset' // Specify the purpose for the API
      });

      // Start the timer for OTP cooldown/expiration display
      setOtpTimer(resp.data.expiresIn);
      const countdown = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setIsOtpSent(true);
      setStep(2); // Move to OTP/New Password input

    } catch (err) {
      const message = err.response?.data?.message || "Failed to request code. Please verify the email or try again later.";
      setError(message);
      setIsOtpSent(false);
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2 Handler: Submit Reset Password ---
  const handleSubmitReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation password do not match.");
      setLoading(false);
      return;
    }
    
    if (newPassword.length < 8) {
        setError("Password must be at least 8 characters long.");
        setLoading(false);
        return;
    }

    try {
      const endpoint = API_URL + "/api/auth/reset-password"; 
      
      const resp = await axios.post(endpoint, { 
        email,
        otpCode,
        newPassword
      });

      setSuccessMessage(resp.data.message || "Your password has been successfully reset. Redirecting to login...");
      setStep(3); // Move to success step
      
      // Optional: Redirect to login after a delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);

    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || "Password reset failed. Check your OTP and try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };
  
  // --- Helper for formatting timer ---
  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec < 10 ? '0' : ''}${sec}`;
  };


  // --- Render Content Based on Step ---
  const renderContent = () => {
    switch (step) {
      case 1: // Enter Email
        return (
          <Form onSubmit={handleRequestOtp}>
            <div className="img_cont">
              <h3>Reset Your Password</h3>
              <p>Enter the email address associated with your account to receive a one-time verification code.</p>
            </div>
            <Form.Group className="mb-4" controlId="ResetEmail">
              <InputGroup>
                <InputGroup.Text className="form_icon"><Mail /></InputGroup.Text>
                <Form.Control
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onInput={(e) => setEmail(e.currentTarget.value)}
                  disabled={loading}
                  required
                />
              </InputGroup>
            </Form.Group>
            <div className="button_group">
              <button
                disabled={loading || !email}
                className="next_btn"
                type="submit"
              >
                <Send size={18} className="mr-2" style={{marginRight: '8px'}} />
                {loading ? "Sending Code..." : "Send Verification Code"}
              </button>
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <a href="/login" className="back-link">
                  &larr; Back to Login
                </a>
            </div>
          </Form>
        );

      case 2: // Enter OTP & New Password
        return (
          <Form onSubmit={handleSubmitReset}>
            <div className="img_cont">
              <h3>Enter Code and New Password</h3>
              <p className="text-sm text-green-700 font-medium mb-3">Code sent to: **{email}**</p>
              <p>
                Please check your inbox for the 6-digit code. The code expires in <span className="font-semibold text-red-600">{formatTime(otpTimer)}</span>.
              </p>
            </div>
            
            {/* OTP Input */}
            <Form.Group className="mb-3" controlId="OtpCode">
              <InputGroup>
                <InputGroup.Text className="form_icon"><Key /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Verification Code (6 digits)"
                  value={otpCode}
                  onInput={(e) => setOtpCode(e.currentTarget.value)}
                  maxLength={6}
                  disabled={loading}
                  required
                />
              </InputGroup>
            </Form.Group>
            
            {/* New Password Input */}
            <Form.Group className="mb-3" controlId="NewPassword">
              <InputGroup>
                <InputGroup.Text className="form_icon"><Lock /></InputGroup.Text>
                <Form.Control
                  type="password"
                  placeholder="New Password (min 8 chars)"
                  value={newPassword}
                  onInput={(e) => setNewPassword(e.currentTarget.value)}
                  minLength={8}
                  disabled={loading}
                  required
                />
              </InputGroup>
            </Form.Group>
            
            {/* Confirm Password Input */}
            <Form.Group className="mb-4" controlId="ConfirmPassword">
              <InputGroup>
                <InputGroup.Text className="form_icon"><Lock /></InputGroup.Text>
                <Form.Control
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onInput={(e) => setConfirmPassword(e.currentTarget.value)}
                  minLength={8}
                  disabled={loading}
                  required
                />
              </InputGroup>
            </Form.Group>

            <div className="button_group">
              <button
                disabled={loading || !otpCode || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="next_btn"
                type="submit"
              >
                <RotateCcw size={18} className="mr-2" style={{marginRight: '8px'}} />
                {loading ? "Resetting Password..." : "Reset Password"}
              </button>
              <button
                type="button"
                className="resend_btn"
                onClick={handleRequestOtp}
                disabled={otpTimer > 0 || loading}
              >
                {otpTimer > 0 ? `Resend available in ${formatTime(otpTimer)}` : "Resend Code"}
              </button>
            </div>
          </Form>
        );

      case 3: // Success
        return (
          <div className="img_cont">
            <CheckCircle size={48} className="text-green-600 mb-4" />
            <h3>Password Reset Complete!</h3>
            <p className="text-lg font-semibold text-green-700">{successMessage}</p>
            <p className="text-gray-600">You are being redirected to the login page now.</p>
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <a href="/login" className="back-link">
                  Click here if you are not redirected.
                </a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };


  return (
    <>
      <style>{`
        .login_cont {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background-color: #f7f9fc;
          font-family: 'Inter', sans-serif;
        }
        .form_cont {
          width: 100%;
          margin: 20px;
        }
        .form_card {
          padding: 30px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }
        .img_cont {
          text-align: center;
          margin-bottom: 30px;
        }
        .img_cont img {
          margin-bottom: 10px;
        }
        .img_cont h3 {
          font-size: 1.5rem;
          color: #1e3a8a;
          font-weight: 700;
        }
        .img_cont p {
          color: #6b7280;
          font-size: 0.95rem;
          margin-top: 10px;
        }
        .form_icon {
          background-color: #eef2ff !important;
          border-right: none !important;
          color: #1e3a8a !important;
          border-top-left-radius: 8px !important;
          border-bottom-left-radius: 8px !important;
        }
        .form-control {
          border-left: none !important;
          border-top-right-radius: 8px !important;
          border-bottom-right-radius: 8px !important;
          height: 48px;
        }
        .form-control:focus {
          box-shadow: 0 0 0 0.25rem rgba(40, 60, 175, 0.25) !important;
          border-color: #4f46e5 !important;
        }
        .next_btn, .resend_btn {
          width: 100%;
          border-radius: 8px;
          padding: 12px;
          font-size: 1rem;
          font-weight: 600;
          transition: background-color 0.3s, transform 0.1s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .next_btn {
          background-color: #1e3a8a;
          color: white;
          border: none;
          margin-bottom: 10px;
        }
        .next_btn:hover:not(:disabled) {
          background-color: #1d4ed8;
        }
        .next_btn:disabled {
          background-color: #93c5fd;
          cursor: not-allowed;
        }
        .resend_btn {
            background-color: #f0f4f8;
            color: #475569;
            border: 1px solid #cbd5e1;
            margin-top: 5px;
        }
        .resend_btn:hover:not(:disabled) {
            background-color: #e2e8f0;
        }
        .resend_btn:disabled {
            cursor: not-allowed;
            color: #94a3b8;
            background-color: #f1f5f9;
        }
        .button_group {
          margin-top: 20px;
        }
        .back-link {
          color: #1e3a8a;
          font-size: 0.9em;
          text-decoration: none;
        }
        .back-link:hover {
          text-decoration: underline;
        }
      `}</style>
      <div className="login_cont">
        <div className="form_cont">
          <Container fluid className="form_card">
            <Row>
              <Col>
                <div className="img_cont">
                  {/* Assuming /nest-logo.png is available or using a placeholder */}
                  <img src="/nest-logo.png" alt="Nest Logo" />
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                {/* Error message display */}
                {error && (
                  <p style={{ color: "red", textAlign: "center", marginBottom: "15px", fontWeight: "500" }}>{error}</p>
                )}
                
                {renderContent()}

              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
}
